/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import BreadCrumbs from "../../../components/BreadCrumbs";
import {
  CartLoading,
  CartLoadingAllocation,
  CartStatus,
  Gate
} from "../../../redux/cartLoadingSlice";
import {
  useCreateGroupOrderMutation,
  useLazyGetCartLoadingDetailQuery,
  useLazyGetCartsLoadForRestaurantByRestaurantIdQuery,
  useUpdateCartLoadingStateMutation,
} from "../../../redux/services/DistributionService";
import "./index.css";
import SupervisorView from "./SupervisorView";

// Interface for order groups
interface OrderGroup {
  id: string;
  name: string;
  orders: string[];
  color: string;
}

const CartLoadingManagement: React.FC = () => {
  const [currentUser] = useState<string>("TL Mike");
  const [selectedCart, setSelectedCart] = useState<string>("");
  const [supervisorTab, setSupervisorTab] = useState<string>("carts");
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);
  const [cart, setCart] = useState<CartLoading | undefined>(undefined);
  const [orderGroups, setOrderGroups] = useState<OrderGroup[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // API call for fetching carts
  const [triggerGetCarts, { data: cartsData, isLoading, error }] =
    useLazyGetCartsLoadForRestaurantByRestaurantIdQuery();

  const [
    triggerGetCartDetail,
    { data: cartDetailData, isLoading: isCartDetailLoading },
  ] = useLazyGetCartLoadingDetailQuery();
  const [createGroupOrder, { isLoading: isCreatingGroupOrder }] =
    useCreateGroupOrderMutation();

  // State for transformed data
  const [carts, setCarts] = useState<CartLoading[]>([]);
  const [gates, setGates] = useState<Gate[]>([]);
  const [viewingCart, setViewingCart] = useState<string>("");
  const [updatingCarts, setUpdatingCarts] = useState<Record<string, string>>(
    {}
  );

  const [cartAllocations, setCartAllocations] = useState<
    CartLoadingAllocation[]
  >([]);

  const RestID = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  // Function to filter out carts with no orders
  const filterCartsWithOrders = (carts: CartLoading[]): CartLoading[] => {
    return carts.filter(cart => {
      const hasIndividualOrders = cart.orders && cart.orders.length > 0;
      const hasGroupOrders = cart.groupOrders && cart.groupOrders.length > 0;
      
      // Filter out group orders that are "Issued by Kitchen"
      if (hasGroupOrders) {
        const activeGroupOrders = cart.groupOrders?.filter(group => 
          group.orders?.some(order => order.orderStatus !== "Issued by Kitchen")
        );
        
        // Update cart's group orders if needed
        if (activeGroupOrders && activeGroupOrders.length !== cart.groupOrders?.length) {
          cart.groupOrders = activeGroupOrders;
        }
      }
      
      // Keep cart if it has any active orders (individual or group)
      const hasActiveIndividualOrders = cart.orders && cart.orders.length > 0;
      const hasActiveGroupOrders = cart.groupOrders && cart.groupOrders.length > 0;
      
      return hasActiveIndividualOrders || hasActiveGroupOrders;
    });
  };

  // Function to check if an order is "Issued by Kitchen"
  const isOrderIssuedByKitchen = (order: any): boolean => {
    return order.orderStatus === "Issued by Kitchen";
  };

  // Function to filter out "Issued by Kitchen" orders from a cart
  const filterIssuedByKitchenOrders = (cart: CartLoading): CartLoading => {
    if (!cart) return cart;

    // Filter individual orders
    const filteredIndividualOrders = cart.orders?.filter(
      order => !isOrderIssuedByKitchen(order)
    ) || [];

    // Filter group orders - remove entire groups where all orders are "Issued by Kitchen"
    const filteredGroupOrders = cart.groupOrders?.filter(group => {
      // Check if any order in the group is NOT "Issued by Kitchen"
      return group.orders?.some(order => !isOrderIssuedByKitchen(order));
    }).map(group => ({
      ...group,
      // Filter out "Issued by Kitchen" orders from within the group
      orders: group.orders?.filter(order => !isOrderIssuedByKitchen(order)) || []
    })) || [];

    return {
      ...cart,
      orders: filteredIndividualOrders,
      groupOrders: filteredGroupOrders
    };
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchCarts = async () => {
      setIsInitialLoading(true);
      setHasError(false);
      try {
        const restaurantId = RestID;
        await triggerGetCarts(restaurantId).unwrap();
      } catch (err) {
        console.error("Failed to fetch carts:", err);
        setHasError(true);
      } finally {
        setIsInitialLoading(false);
      }
    };

    if (RestID) {
      fetchCarts();
    }
  }, [triggerGetCarts, RestID]);

  const [updateCartLoadingState] = useUpdateCartLoadingStateMutation();

  // Helper function to get grouped order IDs from cart
  const getGroupedOrderIdsFromCart = (cart: CartLoading): string[] => {
    if (!cart.groupOrders || !Array.isArray(cart.groupOrders) || cart.groupOrders.length === 0) {
      console.warn(`No group orders found for cart: ${cart.id}`);
      return [];
    }
    
    const validGroupOrderIds = cart.groupOrders
      .filter(group => group && group.id)
      .map(group => group.id);
    
    return validGroupOrderIds;
  };

  const mapApiStatusToCartStatus = (apiStatus: string): CartStatus => {
    if (!apiStatus) return "Pending";

    const statusMap: Record<string, CartStatus> = {
      Pending: "Pending",
      LoadingStarted: "LoadingStarted",
      CratesCollected: "CratesCollected",
      TLItemsVerification: "TLItemsVerification",
      CartPersonVerification: "CartPersonVerification",
      GateAssigned: "GateAssigned",
      AtGate: "AtGate",
      CartCanLeave: "CartCanLeave",
    };

    return statusMap[apiStatus] || "Pending";
  };

  // Function to refresh carts data and filter out completed orders
  const refreshCartsData = async () => {
    try {
      const result = await triggerGetCarts(RestID).unwrap();
      
      // Filter out "Issued by Kitchen" orders from the result
      const filteredCarts = result.map((cart: any) => {
        // Filter individual orders
        const filteredOrders = cart.orders?.filter(
          (order: any) => order.orderStatus !== "Issued by Kitchen"
        ) || [];

        // Filter group orders
        const filteredGroupOrders = cart.groupOrders?.filter((group: any) => {
          // Check if any order in the group is NOT "Issued by Kitchen"
          return group.orders?.some((order: any) => order.orderStatus !== "Issued by Kitchen");
        }).map((group: any) => ({
          ...group,
          orders: group.orders?.filter((order: any) => order.orderStatus !== "Issued by Kitchen") || []
        })) || [];

        return {
          ...cart,
          orders: filteredOrders,
          groupOrders: filteredGroupOrders
        };
      });

      // Filter out carts with no orders
      const cartsWithOrders = filteredCarts.filter((cart: any) => 
        (cart.orders && cart.orders.length > 0) || 
        (cart.groupOrders && cart.groupOrders.length > 0)
      );

      return cartsWithOrders;
    } catch (error) {
      console.error("Failed to refresh carts data:", error);
      throw error;
    }
  };

  // Handle manual refresh
  const handleRefreshCarts = async () => {
    setIsRefreshing(true);
    try {
      const refreshedCarts = await refreshCartsData();
      if (refreshedCarts) {
        // Transform the refreshed carts to match the expected format
        const transformedRefreshedCarts = refreshedCarts.map((cart: any) => {
          const leaveTime = cart.loadingConfigurations?.leaveTime || null;
          const apiStatus = cart.activeState;
          const mappedStatus = mapApiStatusToCartStatus(apiStatus);

          return {
            id: cart.id,
            displayName: cart.displayName || "Unknown Cart",
            rider: cart.owner?.displayName || "Unknown Rider",
            route: "",
            distance: cart.loadingConfigurations?.distance || 0,
            status: mappedStatus,
            gateId: cart.currentAssignedGate || null,
            loadingStartedAt: cart.startedTime
              ? new Date(cart.startedTime._seconds * 1000).toISOString()
              : null,
            estimatedLeaveTime: leaveTime,
            cratesCollectedAt: null,
            itemsVerifiedAt: null,
            gateAssignedAt: null,
            arrivedAtGateAt: null,
            readyToGoAt: null,
            owner: cart.owner,
            orders: cart.orders || [],
            groupOrders: cart.groupOrders || [],
            startedTime: cart.startedTime,
            inventoryData: cart.inventoryData || {},
            loadingConfigurations: cart.loadingConfigurations || {},
            currentAssignedGate: cart.currentAssignedGate || null,
            activeState: cart.activeState || "Pending",
          };
        });
        
        setCarts(transformedRefreshedCarts);

        // Update gates as well
        const assignedGates = refreshedCarts
          .map((cart: any) => cart.assignedGate)
          .filter(Boolean);

        const allGates = ["A", "B", "C", "D", "E"].map((gateId) => ({
          id: gateId,
          name: `Gate ${gateId}`,
          assignedCart: assignedGates.includes(gateId)
            ? refreshedCarts.find(
                (cart: any) => cart.loadingConfigurations?.assignedGate === gateId
              )?.id
            : null,
        }));

        setGates(allGates);
        
        toast.success("Cart list refreshed successfully", {
          toastId: "refresh-success",
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Failed to refresh carts:", error);
      toast.error("Failed to refresh cart list. Please try again.", {
        toastId: "refresh-error",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (cartsData) {
      // Filter out "Issued by Kitchen" orders first
      const filteredCartsData = cartsData.map((cart: any) => {
        // Filter individual orders
        const filteredOrders = cart.orders?.filter(
          (order: any) => order.orderStatus !== "Issued by Kitchen"
        ) || [];

        // Filter group orders
        const filteredGroupOrders = cart.groupOrders?.filter((group: any) => {
          // Check if any order in the group is NOT "Issued by Kitchen"
          return group.orders?.some((order: any) => order.orderStatus !== "Issued by Kitchen");
        }).map((group: any) => ({
          ...group,
          orders: group.orders?.filter((order: any) => order.orderStatus !== "Issued by Kitchen") || []
        })) || [];

        return {
          ...cart,
          orders: filteredOrders,
          groupOrders: filteredGroupOrders
        };
      });

      // Filter out carts with no orders
      const cartsWithOrders = filteredCartsData.filter((cart: any) => 
        (cart.orders && cart.orders.length > 0) || 
        (cart.groupOrders && cart.groupOrders.length > 0)
      );

      const transformedCarts: CartLoading[] = cartsWithOrders.map((cart: any) => {
        const leaveTime = cart.loadingConfigurations?.leaveTime || null;

        const apiStatus = cart.activeState;
        const mappedStatus = mapApiStatusToCartStatus(apiStatus);

        return {
          id: cart.id,
          displayName: cart.displayName || "Unknown Cart",
          rider: cart.owner?.displayName || "Unknown Rider",
          route: "",
          distance: cart.loadingConfigurations?.distance || 0,
          status: mappedStatus,
          gateId: cart.currentAssignedGate || null,
          loadingStartedAt: cart.startedTime
            ? new Date(cart.startedTime._seconds * 1000).toISOString()
            : null,
          estimatedLeaveTime: leaveTime,
          cratesCollectedAt: null,
          itemsVerifiedAt: null,
          gateAssignedAt: null,
          arrivedAtGateAt: null,
          readyToGoAt: null,
          owner: cart.owner,
          orders: cart.orders || [],
          groupOrders: cart.groupOrders || [],
          startedTime: cart.startedTime,
          inventoryData: cart.inventoryData || {},
          loadingConfigurations: cart.loadingConfigurations || {},
          currentAssignedGate: cart.currentAssignedGate || null,
          activeState: cart.activeState || "Pending",
        };
      });

      setCarts(transformedCarts);

      const assignedGates = cartsWithOrders
        .map((cart: any) => cart.assignedGate)
        .filter(Boolean);

      const allGates = ["A", "B", "C", "D", "E"].map((gateId) => ({
        id: gateId,
        name: `Gate ${gateId}`,
        assignedCart: assignedGates.includes(gateId)
          ? cartsWithOrders.find(
              (cart: any) => cart.loadingConfigurations?.assignedGate === gateId
            )?.id
          : null,
      }));

      setGates(allGates);
    }
  }, [cartsData]);

  const getTimeRemaining = (leaveTime: string | null): string => {
    if (!leaveTime) return "-";

    try {
      const target = new Date(leaveTime).getTime();
      const now = Date.now();
      const diff = target - now;
      const minutes = Math.round(diff / (1000 * 60));

      if (minutes <= 0) return "Overdue";
      return `${minutes}m`;
    } catch (error) {
      console.error("Error parsing time:", error);
      return "-";
    }
  };

  const updateCartState = async (
    cartId: string,
    status: string,
    gateId?: string,
    groupOrderIds?: string[]
  ): Promise<void> => {
    const previousCarts = [...carts];
    const previousCart = selectedCart === cartId ? cart : undefined;

    try {
      // Set loading state BEFORE making the API call
      setUpdatingCarts((prev) => ({
        ...prev,
        [`${cartId}-${status}`]: "processing",
      }));

      // Make API call FIRST
      const updateData: any = {
        cartId,
        status,
        restaurantId: RestID
      };

      // Add gateId if provided (existing functionality)
      if (gateId) {
        updateData.assignedGate = gateId;
      }

      const result = await updateCartLoadingState(updateData).unwrap();

      // Only update local state AFTER successful API response
      setCarts((prevCarts) =>
        prevCarts.map((cartItem) =>
          cartItem.id === cartId
            ? {
                ...cartItem,
                status: status as CartStatus,
                ...(status === "LoadingStarted" && {
                  loadingStartedAt: new Date().toISOString(),
                }),
                ...(status === "CratesCollected" && {
                  cratesCollectedAt: new Date().toISOString(),
                }),
                ...(status === "TLItemsVerification" && {
                  itemsVerifiedAt: new Date().toISOString(),
                }),
                ...(status === "CartPersonVerification" && {
                  gateAssignedAt: new Date().toISOString(),
                  gateId: gateId || cartItem.gateId,
                }),
                ...(status === "GateAssigned" && {
                  gateAssignedAt: new Date().toISOString(),
                  gateId: gateId,
                }),
                ...(status === "AtGate" && {
                  arrivedAtGateAt: new Date().toISOString(),
                }),
                ...(status === "CartCanLeave" && {
                  readyToGoAt: new Date().toISOString(),
                }),
              }
            : cartItem
        )
      );

      if (selectedCart === cartId) {
        setCart((prevCart) =>
          prevCart?.id === cartId
            ? {
                ...prevCart,
                status: status as CartStatus,
                gateId: gateId || prevCart.gateId,
              }
            : prevCart
        );
      }

      // Show success toast AFTER state update
      const successMessages: Record<string, string> = {
        LoadingStarted: "Loading started successfully",
        CratesCollected: "Crates collected marked",
        TLItemsVerification: "TL items verification marked",
        CartPersonVerification: "Cart person verification marked",
        GateAssigned: `Gate ${gateId} assigned`,
        AtGate: "Arrived at gate marked",
        CartCanLeave: "Cart can leave marked",
      };

      toast.success(successMessages[status] || "Status updated successfully", {
        toastId: `success-${cartId}-${status}-${Date.now()}`,
        autoClose: 3000,
      });
      
      // If status is CartCanLeave, refresh the carts data to filter out completed orders
      if (status === "CartCanLeave") {
        const refreshedCarts = await refreshCartsData();
        if (refreshedCarts) {
          // Update local state with refreshed data
          const transformedRefreshedCarts = refreshedCarts.map((cart: any) => ({
            ...cart,
            status: mapApiStatusToCartStatus(cart.activeState),
          }));
          setCarts(transformedRefreshedCarts);
        }
      }
      
      // Refresh cart detail if this cart is currently selected
      if (selectedCart === cartId) {
        triggerGetCartDetail({ restId: RestID, cartId });
      }
    } catch (error) {
      console.error(`Failed to update cart state to ${status}:`, error);

      // Revert to previous state on error
      setCarts(previousCarts);

      if (selectedCart === cartId && previousCart) {
        setCart(previousCart);
        triggerGetCartDetail({ restId: RestID, cartId });
      }

      const errorMessages: Record<string, string> = {
        LoadingStarted: "Failed to start loading",
        CratesCollected: "Failed to mark crates collected",
        TLItemsVerification: "Failed to mark TL items verification",
        CartPersonVerification: "Failed to mark cart person verification",
        GateAssigned: "Failed to assign gate",
        AtGate: "Failed to mark arrived at gate",
        CartCanLeave: "Failed to mark cart can leave",
      };

      toast.dismiss();
      toast.error(errorMessages[status] || "Failed to update status", {
        toastId: `error-${cartId}-${status}-${Date.now()}`,
      });
    } finally {
      // Remove loading state
      setUpdatingCarts((prev) => {
        const newState = { ...prev };
        delete newState[`${cartId}-${status}`];
        return newState;
      });
    }
  };

  const handleViewCartDetails = async (cartId: string) => {
    setIsDetailLoading(true);
    setViewingCart(cartId);
    setSelectedCart(cartId);

    const selectedCartData = carts.find((c) => c.id === cartId);
    setCart(selectedCartData);

    try {
      await triggerGetCartDetail({ restId: RestID, cartId }).unwrap();
      setSupervisorTab("loading");
    } catch (error) {
      console.error("Failed to fetch cart details:", error);
      toast.error("Failed to load cart details");
    } finally {
      setIsDetailLoading(false);
      setViewingCart("");
    }
  };

  const handleSelectOrdersStart = async (cartId: string) => {
    setSelectedCart(cartId);

    // Find basic cart data from local state
    const selectedCartData = carts.find((c) => c.id === cartId);
    setCart(selectedCartData);

    // Reset order groups
    setOrderGroups([]);

    try {
      setIsDetailLoading(true);

      // Force refresh cart detail data to get latest state
      await triggerGetCartDetail({ restId: RestID, cartId }).unwrap();

      setSupervisorTab("order-selection");
    } catch (error) {
      console.error("Failed to fetch cart details:", error);
      toast.error("Failed to load order details");
      setSupervisorTab("order-selection");
    } finally {
      setIsDetailLoading(false);
    }
  };

  // Handler for proceeding to grouped orders view
  const handleProceedToGroupedOrders = (groups: OrderGroup[]) => {
    setOrderGroups(groups);
    setSupervisorTab("grouped-orders");
  };

  const handleConfirmGroups = async () => {
    if (cart) {
      const hasCurrentLoadingDoc = cartDetailData?.currentLoadingDoc !== null;
      const baseGroupOrders = orderGroups.map(group => ({
        restaurantId: RestID,
        cartId: cart.id,
        orderIds: group.orders
      }));

      // Create the final payload
      let payload;

      if (!hasCurrentLoadingDoc && baseGroupOrders.length > 0) {
        payload = {
          groupOrders: [
            {
              ...baseGroupOrders[0],
              cartLoadingState: "LoadingStarted"
            },
            ...baseGroupOrders.slice(1)
          ]
        };
      } else {
        payload = {
          groupOrders: baseGroupOrders
        };
      }

      try {
        const result = await createGroupOrder(payload).unwrap();
        
        // Update local cart state to reflect LoadingStarted status
        setCarts(prevCarts => 
          prevCarts.map(cartItem => 
            cartItem.id === cart.id 
              ? {
                  ...cartItem,
                  status: "LoadingStarted" as CartStatus,
                  loadingStartedAt: new Date().toISOString()
                }
              : cartItem
          )
        );

        // Also update the current cart if it's selected
        setCart(prevCart => 
          prevCart ? {
            ...prevCart,
            status: "LoadingStarted" as CartStatus,
            loadingStartedAt: new Date().toISOString()
          } : prevCart
        );

        // Refresh the carts data from server to get latest state
        await refreshCartsData();
        
        // Show success message
        toast.success("Orders grouped successfully and loading started!");
        
        // Navigate to loading tab instead of carts
        setSupervisorTab("loading");
        
      } catch (error) {
        console.error("Failed to create group orders:", error);
        toast.error("Failed to create group orders. Please try again.");
      }
    }
  };

  // Handler for back to order selection
  const handleBackToOrderSelection = () => {
    setSupervisorTab("order-selection");
  };

  // Handler for back to cart list
  const handleBackToCartList = () => {
    setSupervisorTab("carts");
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      Pending: "cart-status-pending",
      LoadingStarted: "cart-status-loading-started",
      CratesCollected: "cart-status-crates-collected",
      TLItemsVerification: "cart-status-items-verification",
      CartPersonVerification: "cart-status-cart-person-verification",
      GateAssigned: "cart-status-gate-assigned",
      AtGate: "cart-status-at-gate",
      CartCanLeave: "cart-status-cart-can-leave",
    };
    return colors[status] || "cart-status-pending";
  };

  const getStatusText = (status: string): string => {
    const texts: Record<string, string> = {
      Pending: "Pending",
      LoadingStarted: "Loading Started",
      CratesCollected: "Crates Collected",
      TLItemsVerification: "TL Items Verification",
      CartPersonVerification: "Cart Person Verification",
      GateAssigned: "Gate Assigned",
      AtGate: "At Gate",
      CartCanLeave: "Cart Can Leave",
    };
    return texts[status] || status;
  };

  const formatTime = (timestamp: string | null): string => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const selectedOnecart = carts.find((c) => c.id === selectedCart);

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />

      <div className="cart-loading-container">
        <div className="cart-loading-content">
          <SupervisorView
            supervisorTab={supervisorTab}
            setSupervisorTab={setSupervisorTab}
            carts={carts}
            cartAllocations={cartAllocations}
            gates={gates}
            cart={cart}
            getStatusColor={getStatusColor}
            getStatusText={getStatusText}
            formatTime={formatTime}
            getTimeRemaining={getTimeRemaining}
            updateCartState={updateCartState}
            setSelectedCart={handleViewCartDetails}
            setSelectedCartForOrderSelection={handleSelectOrdersStart}
            cartDetailData={cartDetailData}
            isCartDetailLoading={isCartDetailLoading || isDetailLoading}
            viewingCart={viewingCart}
            isDetailLoading={isDetailLoading}
            updatingCarts={updatingCarts}
            orderGroups={orderGroups}
            onProceedToGroupedOrders={handleProceedToGroupedOrders}
            onConfirmGroups={handleConfirmGroups}
            onBackToOrderSelection={handleBackToOrderSelection}
            onBackToCartList={handleBackToCartList}
            isCreatingGroupOrder={isCreatingGroupOrder}
            isInitialLoading={isInitialLoading}
            hasError={hasError}
            onRefreshCarts={handleRefreshCarts}
            isRefreshing={isRefreshing}
          />
        </div>
      </div>
    </>
  );
};

export default CartLoadingManagement;